import React, { createContext, useContext, useReducer, useEffect } from 'react';

import useCustomers from 'hooks/queries/useCustomers';
import SessionStorage from 'services/session-storage';
import { Customer, Site } from 'types';
import useSites from 'hooks/queries/useSites';

type Action =
    | {
        type: 'SET_SELECTED_COUNTRY_CODE';
        id: string;
    }
    | {
        type: 'SET_SELECTED_CUSTOMER_ID';
        id: string;
      }
    | {
        type: 'SET_SELECTED_SITE_ID';
        id: string;
      };

export type Dispatch = (action: Action) => void;

type State = {
    selectedCountryCode: string;
    selectedCustomerId: string;
    selectedSiteId: string;
};

type GlobalFilterProps = State & {
    customers: Customer[];
    sites: Site[];
    isLoading: boolean;
};

const GlobalFilterStateContext = createContext<GlobalFilterProps | undefined>(
    undefined
);
const GlobalFilterDispatchContext = createContext<Dispatch | undefined>(
    undefined
);

const filterReducer = (state: State, action: Action) => {
    switch (action.type) {
        case 'SET_SELECTED_COUNTRY_CODE': {
            return { ...state, selectedCountryCode: action.id };
        }
        case 'SET_SELECTED_CUSTOMER_ID': {
            return { ...state, selectedCustomerId: action.id };
        }
        case 'SET_SELECTED_SITE_ID': {
            return { ...state, selectedSiteId: action.id };
        }
    }
};

const getInitialState = () => ({
    selectedCountryCode: SessionStorage.getItem('countryCode', ''),
    selectedCustomerId: SessionStorage.getItem('customerId', ''),
    selectedSiteId: SessionStorage.getItem('siteId', '')
});

export const idExists = (id: string, arr: any[]): boolean => {
    return arr.some(item => item.id === id);
};

export function GlobalFilterProvider({
    children
}: {
    children: React.ReactNode;
}) {
    const [state, dispatch] = useReducer(filterReducer, getInitialState());
    const { data: customers, isLoading: isLoadingCustomers } = useCustomers();
    const { data: sites, isLoading: isLoadingSites } = useSites();

    useEffect(() => {
        if (customers) {
            // Reset selected customerId if the currently stored id doesn't exist in customer payload
            if (!idExists(state.selectedCustomerId, customers)) {
                dispatch({
                    type: 'SET_SELECTED_CUSTOMER_ID',
                    id: ''
                });
            }
        }
    }, [customers, state.selectedCustomerId]);

    useEffect(() => {
        if (sites) {
            // Reset selected siteId if the currently stored id doesn't exist in customer payload
            if (!idExists(state.selectedSiteId, sites)) {
                dispatch({
                    type: 'SET_SELECTED_SITE_ID',
                    id: ''
                });
            }
        }
    }, [sites, state.selectedSiteId]);

    useEffect(() => {
        SessionStorage.setItem('countryCode', state.selectedCountryCode);
    }, [state.selectedCountryCode]);

    useEffect(() => {
        SessionStorage.setItem('customerId', state.selectedCustomerId);
    }, [state.selectedCustomerId]);

    useEffect(() => {
        SessionStorage.setItem('siteId', state.selectedSiteId);
    }, [state.selectedSiteId]);

    const value = React.useMemo(
        () => ({
            ...state,
            isLoading: isLoadingCustomers || isLoadingSites,
            customers: customers ? customers : [],
            sites: sites ? sites : []
        }),
        [state, isLoadingCustomers, isLoadingSites, customers, sites]
    );

    return (
        <GlobalFilterStateContext.Provider value={value}>
            <GlobalFilterDispatchContext.Provider value={dispatch}>
                {children}
            </GlobalFilterDispatchContext.Provider>
        </GlobalFilterStateContext.Provider>
    );
}

export const useGlobalFilterState = (): GlobalFilterProps => {
    const context = useContext(GlobalFilterStateContext);

    if (context === undefined) {
        throw new Error(
            'useGlobalFilter must be used within a GlobalFilterProvider'
        );
    }

    return context;
};

export const useGlobalFilterDispatch = (): Dispatch => {
    const context = useContext(GlobalFilterDispatchContext);

    if (context === undefined) {
        throw new Error(
            'useGlobalFilter must be used within a GlobalFilterProvider'
        );
    }

    return context;
};

export const useGlobalFilter = (): [GlobalFilterProps, Dispatch] => {
    return [useGlobalFilterState(), useGlobalFilterDispatch()];
};
